<script setup lang="ts">
import { CheckCheck, History } from "@lucide/vue";
import { computed } from "vue";
import { useOperations } from "@/api/operations";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import OperationItem from "@/components/items/OperationItem.vue";
import { Button } from "@/components/ui/button";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Approvals" } });

const { me } = useSession();
const pending = useOperations(() => ({ status: "awaiting_approval", all: Boolean(me.value?.canApprove) }));
const mine = computed(() => (pending.data.value ?? []).filter((o) => o.principalId === me.value?.id));
const others = computed(() => (pending.data.value ?? []).filter((o) => o.principalId !== me.value?.id));
</script>

<template>
  <div>
    <PageHeader :title="t.approvals.title" :description="t.approvals.description">
      <template #actions>
        <Button as-child variant="outline"><RouterLink to="/changes"><History /> {{ t.changes.title }}</RouterLink></Button>
      </template>
    </PageHeader>

    <ListSkeleton v-if="pending.isPending.value" :rows="3" />
    <ErrorState v-else-if="pending.error.value" :error="pending.error.value" @retry="pending.refetch()" />
    <EmptyState v-else-if="!pending.data.value?.length" :icon="CheckCheck" :title="t.approvals.empty" :description="t.approvals.emptyHint">
      <Button as-child variant="outline"><RouterLink to="/changes">See past changes</RouterLink></Button>
    </EmptyState>
    <div v-else class="flex flex-col gap-8">
      <section v-if="me?.canApprove && others.length" class="flex flex-col gap-3">
        <h2 class="section-title">{{ t.approvals.others }} <span class="font-normal text-muted-foreground">({{ others.length }})</span></h2>
        <OperationItem v-for="op in others" :key="op.id" :op="op" show-requester />
      </section>
      <section v-if="mine.length" class="flex flex-col gap-3">
        <h2 class="section-title">{{ t.approvals.mine }} <span class="font-normal text-muted-foreground">({{ mine.length }})</span></h2>
        <OperationItem v-for="op in mine" :key="op.id" :op="op" />
      </section>
    </div>
  </div>
</template>
